import React, { useRef, useMemo, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Text, Float, Stars } from '@react-three/drei';
import * as THREE from 'three';
import { cards } from '../data/CardContent';

const EnergyCore = ({ isSelecting, speed }) => {
  const coreRef = useRef();
  const innerCoreRef = useRef();
  const ring1Ref = useRef();
  const ring2Ref = useRef();
  const ring3Ref = useRef();
  const particlesRef = useRef();
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    if (coreRef.current) {
      coreRef.current.rotation.x = time * 0.3;
      coreRef.current.rotation.y = time * 0.5;
    }
    
    if (innerCoreRef.current) {
      innerCoreRef.current.rotation.y = -time * 0.8;
      innerCoreRef.current.rotation.z = time * 0.4;
      
      const scale = 1 + Math.sin(time * 2) * 0.1;
      innerCoreRef.current.scale.setScalar(scale);
    }
    
    if (ring1Ref.current) {
      ring1Ref.current.rotation.x = Math.sin(time * 0.5) * 0.2;
      ring1Ref.current.rotation.z = time * 0.3;
    }
    
    if (ring2Ref.current) {
      ring2Ref.current.rotation.x = Math.cos(time * 0.4) * 0.3;
      ring2Ref.current.rotation.y = Math.sin(time * 0.6) * 0.2;
    }
    
    if (ring3Ref.current) {
      ring3Ref.current.rotation.z = -time * 0.4;
      ring3Ref.current.rotation.x = Math.sin(time * 0.3) * 0.1;
    }
    
    if (particlesRef.current) {
      particlesRef.current.rotation.y = time * 0.1;
    }
  });
  
  const coreColor = isSelecting ? '#ffd700' : speed > 2 ? '#06b6d4' : '#6366f1';
  const glowColor = isSelecting ? '#ffaa00' : speed > 2 ? '#0891b2' : '#4f46e5';
  
  return (
    <group position={[0, 0, 0]}>
      <Float speed={3} rotationIntensity={0.2} floatIntensity={0.5}>
        <group>
          <mesh ref={innerCoreRef}>
            <octahedronGeometry args={[0.6, 0]} />
            <meshStandardMaterial
              color={coreColor}
              emissive={coreColor}
              emissiveIntensity={2}
              metalness={0.5}
              roughness={0.2}
            />
          </mesh>
          
          <mesh ref={coreRef}>
            <icosahedronGeometry args={[0.9, 1]} />
            <meshStandardMaterial
              color={coreColor}
              transparent
              opacity={0.6}
              emissive={coreColor}
              emissiveIntensity={0.5}
              metalness={0.8}
              roughness={0.3}
              wireframe
            />
          </mesh>
        </group>
      </Float>
      
      <mesh ref={ring1Ref}>
        <torusGeometry args={[1.8, 0.025, 16, 100]} />
        <meshBasicMaterial color={coreColor} transparent opacity={0.8} />
      </mesh>
      
      <mesh ref={ring2Ref} rotation={[Math.PI / 3, 0, 0]}>
        <torusGeometry args={[2.2, 0.02, 16, 100]} />
        <meshBasicMaterial color={glowColor} transparent opacity={0.6} />
      </mesh>
      
      <mesh ref={ring3Ref} rotation={[0, Math.PI / 4, Math.PI / 6]}>
        <torusGeometry args={[2.7, 0.015, 16, 100]} />
        <meshBasicMaterial color={coreColor} transparent opacity={0.4} />
      </mesh>
      
      <OrbitRings coreColor={coreColor} isSelecting={isSelecting} />
      
      <FloatingParticles ref={particlesRef} coreColor={coreColor} count={80} />
      
      <PointLightGlow coreColor={coreColor} />
    </group>
  );
};

const OrbitRings = ({ coreColor, isSelecting }) => {
  const groupRef = useRef();
  const sphereRefs = useRef([]);
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    sphereRefs.current.forEach((ref, i) => {
      if (ref) {
        const angle = (time * 0.5 + (i * Math.PI * 2) / 3) % (Math.PI * 2);
        const radius = 3.2;
        ref.position.x = Math.cos(angle) * radius;
        ref.position.z = Math.sin(angle) * radius;
        ref.position.y = Math.sin(time * 2 + i) * 0.3;
      }
    });
    
    if (groupRef.current) {
      groupRef.current.rotation.y = time * 0.2;
    }
  });
  
  return (
    <group ref={groupRef}>
      {[0, 1, 2].map((i) => (
        <mesh
          key={i}
          ref={(el) => (sphereRefs.current[i] = el)}
        >
          <sphereGeometry args={[0.12, 16, 16]} />
          <meshBasicMaterial color={coreColor} />
        </mesh>
      ))}
    </group>
  );
};

const FloatingParticles = React.forwardRef(({ coreColor, count }, ref) => {
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      const r = 3.5 + Math.random() * 2;
      pos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = r * Math.cos(phi);
    }
    return pos;
  }, [count]);
  
  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.elapsedTime * 0.05;
    }
  });
  
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.08}
        color={coreColor}
        transparent
        opacity={0.8}
        sizeAttenuation
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
});

FloatingParticles.displayName = 'FloatingParticles';

const PointLightGlow = ({ coreColor }) => {
  return (
    <group>
      <pointLight color={coreColor} intensity={2.5} distance={6} decay={2} />
      <pointLight color={coreColor} intensity={1.5} distance={10} decay={2} position={[3, 3, 3]} />
      <pointLight color={coreColor} intensity={0.8} distance={12} decay={2} position={[-3, -3, -3]} />
    </group>
  );
};

const Card3D = ({ card, position, rotation, scale, isSelecting, holdProgress, onClick }) => {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);
  
  const colorMap = useMemo(() => ({
    'from-orange-400 to-red-500': { color1: '#fb923c', color2: '#ef4444' },
    'from-purple-400 to-pink-500': { color1: '#c084fc', color2: '#f472b6' },
    'from-blue-400 to-cyan-500': { color1: '#60a5fa', color2: '#22d3ee' },
    'from-yellow-400 to-orange-500': { color1: '#facc15', color2: '#f97316' },
    'from-pink-400 to-rose-500': { color1: '#f472b6', color2: '#f43f5e' },
    'from-red-400 to-rose-500': { color1: '#f87171', color2: '#f43f5e' },
    'from-green-400 to-emerald-500': { color1: '#4ade80', color2: '#10b981' },
    'from-indigo-400 to-violet-500': { color1: '#818cf8', color2: '#8b5cf6' },
    'from-cyan-400 to-blue-500': { color1: '#22d3ee', color2: '#3b82f6' },
    'from-amber-400 to-yellow-500': { color1: '#fbbf24', color2: '#facc15' },
    'from-teal-400 to-cyan-500': { color1: '#2dd4bf', color2: '#22d3ee' },
    'from-rose-400 to-red-500': { color1: '#fb7185', color2: '#ef4444' },
    'from-violet-400 to-purple-500': { color1: '#a78bfa', color2: '#a855f7' },
    'from-sky-400 to-blue-500': { color1: '#38bdf8', color2: '#3b82f6' },
    'from-emerald-400 to-teal-500': { color1: '#34d399', color2: '#14b8a6' },
    'from-lime-400 to-green-500': { color1: '#a3e635', color2: '#22c55e' },
    'from-fuchsia-400 to-pink-500': { color1: '#e879f9', color2: '#f472b6' },
    'from-rose-500 to-pink-600': { color1: '#f43f5e', color2: '#db2777' },
  }), []);
  
  const gradientColors = useMemo(() => {
    const colors = colorMap[card.theme.gradient] || { color1: '#6366f1', color2: '#8b5cf6' };
    return {
      color1: new THREE.Color(colors.color1),
      color2: new THREE.Color(colors.color2)
    };
  }, [card, colorMap]);

  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += delta * 0.2;
      
      if (hovered) {
        meshRef.current.scale.lerp(new THREE.Vector3(1.3, 1.3, 1.3), 0.1);
      } else {
        meshRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
      }
    }
  });

  return (
    <Float speed={2} rotationIntensity={0.1} floatIntensity={0.2}>
      <group position={position} rotation={rotation} scale={scale}>
        <mesh
          ref={meshRef}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
          onClick={() => onClick(card)}
          renderOrder={1}
        >
          <boxGeometry args={[1.5, 2.2, 0.08]} />
          <meshStandardMaterial
            color={gradientColors.color1}
            emissive={gradientColors.color1}
            emissiveIntensity={0.3}
            metalness={0.8}
            roughness={0.2}
          />
        </mesh>
        
        <mesh position={[0, 0, 0.05]} renderOrder={2}>
          <planeGeometry args={[1.25, 1.9]} />
          <meshBasicMaterial
            color={gradientColors.color2}
            transparent
            opacity={0.9}
          />
        </mesh>
        
        <Text
          position={[0, 0.5, 0.06]}
          fontSize={0.3}
          color="white"
          anchorX="center"
          anchorY="middle"
          renderOrder={3}
        >
          {card.icon}
        </Text>
        
        <Text
          position={[0, -0.35, 0.06]}
          fontSize={0.15}
          color="white"
          anchorX="center"
          anchorY="middle"
          maxWidth={1.2}
          renderOrder={3}
        >
          {card.theme.name}
        </Text>
        
        <Text
          position={[0, -0.75, 0.06]}
          fontSize={0.1}
          color="rgba(255,255,255,0.6)"
          anchorX="center"
          anchorY="middle"
          renderOrder={3}
        >
          #{card.id}
        </Text>
        
        {isSelecting && (
          <mesh position={[0, 0, 0.045]} renderOrder={0}>
            <planeGeometry args={[1.6, 2.3]} />
            <meshBasicMaterial
              color="#ffd700"
              transparent
              opacity={0.3}
              side={THREE.DoubleSide}
              depthWrite={false}
            />
          </mesh>
        )}
        
        {isSelecting && (
          <mesh position={[0, -1.05 + (holdProgress / 100), 0.06]} renderOrder={4}>
            <boxGeometry args={[1.55, 0.06, 0.03]} />
            <meshBasicMaterial color="#ffd700" />
          </mesh>
        )}
        
        {hovered && (
          <mesh renderOrder={0}>
            <boxGeometry args={[1.6, 2.3, 0.07]} />
            <meshBasicMaterial color="white" transparent opacity={0.2} depthWrite={false} />
          </mesh>
        )}
      </group>
    </Float>
  );
};

const RingRotation = ({ speed, isSelecting }) => {
  const groupRef = useRef();
  const [rotation, setRotation] = useState(0);
  
  useFrame((state, delta) => {
    if (groupRef.current) {
      const rotationSpeed = isSelecting ? speed * 0.1 : speed;
      const newRotation = rotation + delta * rotationSpeed * 0.5;
      setRotation(newRotation);
      groupRef.current.rotation.y = newRotation;
    }
  });
  
  return (
    <group ref={groupRef}>
      {cards.slice(0, 8).map((card, index) => {
        const angle = (index / 8) * Math.PI * 2;
        const radius = 4.5;
        const x = Math.cos(angle) * radius;
        const z = Math.sin(angle) * radius;
        
        return (
          <Card3D
            key={card.id}
            card={card}
            position={[x, 0, z]}
            rotation={[0, -angle, 0]}
            scale={1}
            isSelecting={isSelecting}
            holdProgress={0}
            onClick={() => {}}
          />
        );
      })}
    </group>
  );
};

const Environment = () => {
  return (
    <>
      <ambientLight intensity={0.2} />
      <pointLight position={[12, 12, 12]} intensity={1.2} />
      <pointLight position={[-12, -12, -12]} intensity={0.6} color="#6366f1" />
      <pointLight position={[0, -12, 0]} intensity={0.4} color="#06b6d4" />
      
      <Stars radius={120} depth={60} count={4000} factor={4} saturation={0} fade speed={0.5} />
      
      <fog attach="fog" args={['#0f172a', 12, 35]} />
    </>
  );
};

const Three3DCards = ({ speed = 1, isSelecting, holdProgress, onCardSelect }) => {
  return (
    <div className="w-full h-full">
      <Canvas
        camera={{ position: [0, 4, 12], fov: 50 }}
        gl={{ antialias: true, alpha: true }}
      >
        <color attach="background" args={['#0f172a']} />
        
        <Environment />
        
        <EnergyCore isSelecting={speed > 2} speed={speed} />
        
        <RingRotation speed={speed} isSelecting={isSelecting} />
      </Canvas>
    </div>
  );
};

export default Three3DCards;
